import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { isNull } from "lodash"

import Tab from "@/components/tab/Tab"
import BasicButton from "@/components/button/BasicButton"
import ZoneTabContent from "@/app/settings/metric/zones/components/ZoneTabContent"
import { MIN_MAX, MIN_MAX_PLAYER_METRIC } from "@/app/settings/metric/zones/components/constants"
import { ZoneEditedState } from "@/app/settings/metric/zones/components/type"
import { createZoneSettingState, ZoneSettingType } from "@/stores/settings"
import { createGlobalStore } from "@/stores/global"
import { SpeedZone } from "@/types/zone"
import {
  useSpeedZonesQuery,
  useAccelZonesQuery,
  useDecelZonesQuery,
  useHrZonesQuery,
  useUpsertSpeedZonesMutation,
  useUpsertAccelZonesMutation,
  useUpsertDecelZonesMutation,
  useUpsertHrZonesMutation,
  useUpsertPlayerMetricSpeedActiveMutation,
  useUpsertPlayerMetricAccelActiveMutation,
  useUpsertPlayerMetricDecelActiveMutation,
  useUpsertPlayerMetricHrActiveMutation,
} from "@/hooks/api/useZoneQuery"

import { useNativeBridge } from "@/hooks/useNativeBridge"

import "./zones.scss"

const DEFAULT_TAB_STATES = {
  [ZoneSettingType.SPEED]: null,
  [ZoneSettingType.ACCELERATION]: null,
  [ZoneSettingType.DECELERATION]: null,
  [ZoneSettingType.HR]: null,
}

export default function Page() {
  const { t } = useTranslation()
  const { isAllValid, hasChanges, setHasChanges } = createZoneSettingState()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const { notifySettingChanged } = useNativeBridge()

  const [currentTab, setCurrentTab] = useState<ZoneSettingType>(ZoneSettingType.SPEED)
  const [allStateLoading, setAllStateLoading] = useState(false)
  const [tabStates, setTabStates] = useState<{
    [key in ZoneSettingType]: ZoneEditedState | null
  }>(DEFAULT_TAB_STATES)

  const {
    data: speedZones,
    isLoading: speedLoading,
    refetch: refetchSpeed,
  } = useSpeedZonesQuery({ enabled: true })
  const {
    data: accelZones,
    isLoading: accelLoading,
    refetch: refetchAccel,
  } = useAccelZonesQuery({ enabled: true })
  const {
    data: decelZones,
    isLoading: decelLoading,
    refetch: refetchDecel,
  } = useDecelZonesQuery({ enabled: true })
  const { data: hrZones, isLoading: hrLoading, refetch: refetchHr } = useHrZonesQuery()

  const { mutateAsync: speedMutate } = useUpsertSpeedZonesMutation()
  const { mutateAsync: accelMutate } = useUpsertAccelZonesMutation()
  const { mutateAsync: decelMutate } = useUpsertDecelZonesMutation()
  const { mutateAsync: hrMutate } = useUpsertHrZonesMutation()

  const { mutateAsync: speedActiveMutate } = useUpsertPlayerMetricSpeedActiveMutation()
  const { mutateAsync: accelActiveMutate } = useUpsertPlayerMetricAccelActiveMutation()
  const { mutateAsync: decelActiveMutate } = useUpsertPlayerMetricDecelActiveMutation()
  const { mutateAsync: hrActiveMutate } = useUpsertPlayerMetricHrActiveMutation()

  const tabList = [
    { label: t("speed"), value: ZoneSettingType.SPEED },
    { label: t("acceleration"), value: ZoneSettingType.ACCELERATION },
    { label: t("deceleration"), value: ZoneSettingType.DECELERATION },
    { label: t("heartRate"), value: ZoneSettingType.HR },
  ]

  const transformZones = (
    zones: SpeedZone | undefined,
    fixedNumber: number,
  ): SpeedZone | undefined => {
    if (!zones) {
      return undefined
    }

    return {
      ...zones,
      playerMetrics:
        zones.playerMetrics?.map((metric) => ({
          ...metric,
          max: isNull(metric.max) ? null : Number(metric.max).toFixed(fixedNumber),
        })) || [],
    } as SpeedZone
  }

  const onSaveAll = async () => {
    const saves: Promise<unknown>[] = []
    const savedKeys: ZoneSettingType[] = []
    setAllStateLoading(true)
    notifySettingChanged("zone", "saving")

    try {
      if (hasChanges[ZoneSettingType.SPEED] && tabStates[ZoneSettingType.SPEED]) {
        const data = tabStates[ZoneSettingType.SPEED]
        saves.push(
          speedMutate({
            global: data.global,
            individual: data.individual,
            playerMetrics: data.playerMetrics.map((item) => ({
              idfPlayer: item.idfPlayer,
              max: { code: "maxSpeed", value: item.max },
              active: item.active,
              customMetricActive: item.customMetricActive,
              customMetrics: item.customMetrics?.filter((m) => m.value !== ""),
            })),
          }),
        )
        savedKeys.push(ZoneSettingType.SPEED)
      }

      if (hasChanges[ZoneSettingType.ACCELERATION] && tabStates[ZoneSettingType.ACCELERATION]) {
        const data = tabStates[ZoneSettingType.ACCELERATION]
        saves.push(
          accelMutate({
            global: data.global,
            individual: data.individual,
            playerMetrics: data.playerMetrics.map((item) => ({
              idfPlayer: item.idfPlayer,
              max: { code: "maxAccel", value: item.max },
              active: item.active,
              customMetricActive: item.customMetricActive,
              customMetrics: item.customMetrics?.filter((m) => m.value !== ""),
            })),
          }),
        )
        savedKeys.push(ZoneSettingType.ACCELERATION)
      }

      if (hasChanges[ZoneSettingType.DECELERATION] && tabStates[ZoneSettingType.DECELERATION]) {
        const data = tabStates[ZoneSettingType.DECELERATION]
        saves.push(
          decelMutate({
            global: data.global,
            individual: data.individual,
            playerMetrics: data.playerMetrics.map((item) => ({
              idfPlayer: item.idfPlayer,
              max: { code: "maxDecel", value: item.max },
              active: item.active,
              customMetricActive: item.customMetricActive,
              customMetrics: item.customMetrics?.filter((m) => m.value !== ""),
            })),
          }),
        )
        savedKeys.push(ZoneSettingType.DECELERATION)
      }

      if (hasChanges[ZoneSettingType.HR] && tabStates[ZoneSettingType.HR]) {
        const data = tabStates[ZoneSettingType.HR]
        saves.push(
          hrMutate({
            global: data.global,
            individual: data.global,
            playerMetrics: data.playerMetrics.map((item) => ({
              idfPlayer: item.idfPlayer,
              max: { code: "maxHr", value: item.max },
              active: item.active,
              customMetricActive: item.customMetricActive,
              customMetrics: item.customMetrics?.map((metric, index) => ({
                code: `${metric.code.split("Zone")[0]}Zone${index}`,
                value: metric.value,
                type: metric.type,
              })),
            })),
            maxHr: { code: "maxHr", value: data.maxHr ?? 210 },
          }),
        )
        savedKeys.push(ZoneSettingType.HR)
      }

      await Promise.all(saves)
      setSnackBar({ message: t("messages.successUpdate"), open: true, severity: "success" })
      notifySettingChanged("zone", "update")

      const refetchPromises: Promise<unknown>[] = []
      if (savedKeys.includes(ZoneSettingType.SPEED)) {
        refetchPromises.push(refetchSpeed())
      }
      if (savedKeys.includes(ZoneSettingType.ACCELERATION)) {
        refetchPromises.push(refetchAccel())
      }
      if (savedKeys.includes(ZoneSettingType.DECELERATION)) {
        refetchPromises.push(refetchDecel())
      }
      if (savedKeys.includes(ZoneSettingType.HR)) {
        refetchPromises.push(refetchHr())
      }

      await Promise.all(refetchPromises)

      setTabStates((prev) => {
        const newStates = { ...prev }
        savedKeys.forEach((key) => {
          newStates[key] = null
        })

        return newStates
      })

      savedKeys.forEach((key) => {
        setHasChanges(key, false)
      })
    } catch (error) {
      console.error("Error saving zones:", error)
      notifySettingChanged("zone", "error")
    } finally {
      setAllStateLoading(false)
    }
  }

  const tabContent = useMemo(() => {
    switch (currentTab) {
      case ZoneSettingType.SPEED:
        return (
          <ZoneTabContent
            key="speed"
            type="speed"
            zoneSettingType={ZoneSettingType.SPEED}
            zones={transformZones(speedZones, 1)}
            isLoading={speedLoading}
            mutateAsync={speedActiveMutate}
            minMax={MIN_MAX.speed}
            minMaxPlayerMetric={MIN_MAX_PLAYER_METRIC.acceleration}
            hideSaveButton
            editedData={tabStates[ZoneSettingType.SPEED]}
            setEditedData={(state) =>
              setTabStates((prev) => ({ ...prev, [ZoneSettingType.SPEED]: state }))
            }
          />
        )
      case ZoneSettingType.ACCELERATION:
        return (
          <ZoneTabContent
            key="acceleration"
            type="acceleration"
            zoneSettingType={ZoneSettingType.ACCELERATION}
            zones={transformZones(accelZones, 1)}
            isLoading={accelLoading}
            mutateAsync={accelActiveMutate}
            minMax={MIN_MAX.acceleration}
            minMaxPlayerMetric={MIN_MAX_PLAYER_METRIC.acceleration}
            hideSaveButton
            editedData={tabStates[ZoneSettingType.ACCELERATION]}
            setEditedData={(state) =>
              setTabStates((prev) => ({ ...prev, [ZoneSettingType.ACCELERATION]: state }))
            }
          />
        )
      case ZoneSettingType.DECELERATION:
        return (
          <ZoneTabContent
            key="deceleration"
            type="deceleration"
            zoneSettingType={ZoneSettingType.DECELERATION}
            zones={transformZones(decelZones, 1)}
            isLoading={decelLoading}
            mutateAsync={decelActiveMutate}
            minMax={MIN_MAX.deceleration}
            minMaxPlayerMetric={MIN_MAX_PLAYER_METRIC.acceleration}
            hideSaveButton
            editedData={tabStates[ZoneSettingType.DECELERATION]}
            setEditedData={(state) =>
              setTabStates((prev) => ({ ...prev, [ZoneSettingType.DECELERATION]: state }))
            }
          />
        )
      case ZoneSettingType.HR:
        return (
          <ZoneTabContent
            key="hr"
            type="hr"
            zoneSettingType={ZoneSettingType.HR}
            zones={hrZones}
            isLoading={hrLoading}
            mutateAsync={hrActiveMutate}
            minMax={MIN_MAX.hr}
            minMaxPlayerMetric={MIN_MAX_PLAYER_METRIC.hr}
            hideSaveButton
            editedData={tabStates[ZoneSettingType.HR]}
            setEditedData={(state) =>
              setTabStates((prev) => ({ ...prev, [ZoneSettingType.HR]: state }))
            }
          />
        )
      default:
        return null
    }
  }, [
    currentTab,
    tabStates,
    speedZones,
    accelZones,
    decelZones,
    hrZones,
    speedLoading,
    accelLoading,
    decelLoading,
    hrLoading,
    speedActiveMutate,
    accelActiveMutate,
    decelActiveMutate,
    hrActiveMutate,
  ])

  return (
    <div className="ZoneSettingCont">
      <Tab
        type="filled"
        list={tabList}
        value={currentTab}
        onChange={(_, value) => setCurrentTab(value)}
      />

      <div className="ZoneRowsCont">{tabContent}</div>

      <div className="ZoneButtonCont">
        <BasicButton
          type="primary-contained"
          size="lg"
          onClick={onSaveAll}
          disabled={
            allStateLoading ||
            !Object.values(isAllValid).every(Boolean) ||
            !Object.values(hasChanges).some(Boolean)
          }>
          {t("button.save")}
        </BasicButton>
      </div>
    </div>
  )
}
